import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  getBookings, getPatients, getDashboardStats,
  updateBookingStatus, BookingStatus, Booking
} from "@/services/data";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import {
  LayoutDashboard, Users, ClipboardList, TrendingUp, Home, Building2,
  Search, ChevronDown, CheckCircle, Clock, FlaskConical, XCircle, LogOut
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const statusOptions: BookingStatus[] = ["Pending", "Confirmed", "Sample Collected", "Report Ready", "Cancelled"];

const statusColor: Record<BookingStatus, string> = {
  "Pending": "bg-yellow-100 text-yellow-700",
  "Confirmed": "bg-blue-100 text-blue-700",
  "Sample Collected": "bg-purple-100 text-purple-700",
  "Report Ready": "bg-green-100 text-green-700",
  "Cancelled": "bg-red-100 text-red-700",
};

const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6"];

export default function AdminPage() {
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<BookingStatus | "All">("All");

  const { data: stats, refetch: refetchStats } = useQuery({ queryKey: ["admin-stats"], queryFn: getDashboardStats });
  const { data: bookings, refetch: refetchBookings } = useQuery({ queryKey: ["bookings"], queryFn: getBookings });
  const { data: patients } = useQuery({ queryKey: ["patients"], queryFn: getPatients });

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground mb-4">Admin access required</p>
        <Button onClick={() => navigate("/login")}>Go to Login</Button>
      </div>
    );
  }

  const handleStatusChange = async (bookingId: string, newStatus: BookingStatus) => {
    await updateBookingStatus(bookingId, newStatus);
    refetchBookings();
    refetchStats();
    toast.success(`Status updated to "${newStatus}"`);
  };

  const filtered = (bookings || []).filter(b => {
    const matchSearch = !search || b.patientName.toLowerCase().includes(search.toLowerCase()) || b.id.includes(search);
    const matchStatus = statusFilter === "All" || b.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const revenueData = [
    { name: "Mon", revenue: 4200 },
    { name: "Tue", revenue: 5800 },
    { name: "Wed", revenue: 3400 },
    { name: "Thu", revenue: 7100 },
    { name: "Fri", revenue: 6200 },
    { name: "Sat", revenue: 9400 },
    { name: "Sun", revenue: 8300 },
  ];

  const statusDist = statusOptions.map(s => ({
    name: s,
    value: (bookings || []).filter(b => b.status === s).length,
  }));

  const statCards = [
    { label: "Total Bookings", value: stats?.totalBookings ?? 0, icon: ClipboardList, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Today's Bookings", value: stats?.todayBookings ?? 0, icon: Clock, color: "text-purple-600", bg: "bg-purple-50" },
    { label: "Pending", value: stats?.pendingBookings ?? 0, icon: Clock, color: "text-yellow-600", bg: "bg-yellow-50" },
    { label: "Total Revenue", value: `₹${(stats?.totalRevenue ?? 0).toLocaleString()}`, icon: TrendingUp, color: "text-green-600", bg: "bg-green-50" },
    { label: "Home Collections", value: stats?.homeCollections ?? 0, icon: Home, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Reports Ready", value: stats?.reportReady ?? 0, icon: CheckCircle, color: "text-teal-600", bg: "bg-teal-50" },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      {/* Admin header */}
      <div className="bg-background border-b border-border px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">D+</span>
          </div>
          <div>
            <span className="font-bold text-foreground">DiagnoPlus</span>
            <Badge className="ml-2 text-xs bg-primary/10 text-primary border-0">Admin Panel</Badge>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="text-destructive hover:text-destructive">
          <LogOut className="h-4 w-4 mr-1" /> Logout
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="dashboard">
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard"><LayoutDashboard className="h-4 w-4 mr-1" />Dashboard</TabsTrigger>
            <TabsTrigger value="bookings"><ClipboardList className="h-4 w-4 mr-1" />Bookings</TabsTrigger>
            <TabsTrigger value="patients"><Users className="h-4 w-4 mr-1" />Patients</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {statCards.map(card => (
                <div key={card.label} className="bg-background border border-border rounded-xl p-4">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center mb-3", card.bg)}>
                    <card.icon className={cn("h-5 w-5", card.color)} />
                  </div>
                  <p className="text-xl font-bold text-foreground">{card.value}</p>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Revenue chart */}
              <div className="lg:col-span-2 bg-background border border-border rounded-xl p-5">
                <h3 className="font-semibold text-foreground mb-4">Weekly Revenue</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [`₹${v}`, "Revenue"]} />
                    <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Status distribution */}
              <div className="bg-background border border-border rounded-xl p-5">
                <h3 className="font-semibold text-foreground mb-4">Booking Status</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={statusDist} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value">
                      {statusDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {statusDist.map((s, i) => (
                    <div key={s.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="text-muted-foreground">{s.name}</span>
                      </div>
                      <span className="font-medium text-foreground">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent bookings */}
            <div className="bg-background border border-border rounded-xl p-5">
              <h3 className="font-semibold text-foreground mb-4">Recent Bookings</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-xs text-muted-foreground">
                      <th className="text-left pb-3 pr-4">Patient</th>
                      <th className="text-left pb-3 pr-4">Tests</th>
                      <th className="text-left pb-3 pr-4">Date</th>
                      <th className="text-left pb-3 pr-4">Type</th>
                      <th className="text-left pb-3 pr-4">Status</th>
                      <th className="text-left pb-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bookings || []).slice(0, 5).map(b => (
                      <tr key={b.id} className="border-b border-border/50 last:border-0">
                        <td className="py-3 pr-4">
                          <p className="font-medium text-foreground">{b.patientName}</p>
                          <p className="text-xs text-muted-foreground">{b.patientPhone}</p>
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground max-w-32 truncate">{b.testNames.join(", ")}</td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">{b.slotDate}</td>
                        <td className="py-3 pr-4">
                          {b.collectionType === "Home Collection" ? <Home className="h-3.5 w-3.5 text-primary" /> : <Building2 className="h-3.5 w-3.5 text-muted-foreground" />}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge className={cn("border-0 text-xs", statusColor[b.status])}>{b.status}</Badge>
                        </td>
                        <td className="py-3 text-foreground font-medium">₹{b.totalAmount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search by name or booking ID..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div className="flex gap-2 flex-wrap">
                {(["All", ...statusOptions] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s as BookingStatus | "All")}
                    className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors", statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40")}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {filtered.map(b => (
                <BookingRow key={b.id} booking={b} onStatusChange={handleStatusChange} statusColor={statusColor} statusOptions={statusOptions} />
              ))}
            </div>
          </TabsContent>

          {/* Patients Tab */}
          <TabsContent value="patients">
            <div className="bg-background border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-xs text-muted-foreground">
                    <th className="text-left p-4">Patient</th>
                    <th className="text-left p-4 hidden sm:table-cell">Phone</th>
                    <th className="text-left p-4 hidden md:table-cell">City</th>
                    <th className="text-left p-4 hidden md:table-cell">Gender</th>
                    <th className="text-left p-4">Bookings</th>
                    <th className="text-left p-4 hidden sm:table-cell">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {(patients || []).map(p => (
                    <tr key={p.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                      <td className="p-4">
                        <p className="font-medium text-foreground">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.email}</p>
                      </td>
                      <td className="p-4 hidden sm:table-cell text-muted-foreground">{p.phone}</td>
                      <td className="p-4 hidden md:table-cell text-muted-foreground">{p.city}</td>
                      <td className="p-4 hidden md:table-cell">
                        <Badge variant="secondary" className="text-xs">{p.gender}</Badge>
                      </td>
                      <td className="p-4">
                        <Badge className="bg-primary/10 text-primary border-0 text-xs">{p.bookingIds.length}</Badge>
                      </td>
                      <td className="p-4 hidden sm:table-cell text-xs text-muted-foreground">
                        {new Date(p.createdAt).toLocaleDateString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function BookingRow({ booking, onStatusChange, statusColor, statusOptions }: {
  booking: Booking;
  onStatusChange: (id: string, status: BookingStatus) => void;
  statusColor: Record<BookingStatus, string>;
  statusOptions: BookingStatus[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-background border border-border rounded-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-foreground">{booking.patientName}</p>
            <span className="text-xs text-muted-foreground font-mono">{booking.id}</span>
            <Badge className={cn("border-0 text-xs", statusColor[booking.status])}>{booking.status}</Badge>
            <Badge className={cn("border-0 text-xs", booking.paymentStatus === "Paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700")}>
              {booking.paymentStatus}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">{booking.testNames.join(", ")}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span>📅 {booking.slotDate} · {booking.slotTime}</span>
            <span>{booking.collectionType === "Home Collection" ? `🏠 ${booking.city}` : "🏥 Walk-in"}</span>
            <span className="font-medium text-foreground">₹{booking.totalAmount}</span>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            Update Status <ChevronDown className="h-3 w-3" />
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-1 bg-background border border-border rounded-xl shadow-lg z-20 min-w-44 py-1">
              {statusOptions.map(s => (
                <button
                  key={s}
                  onClick={() => { onStatusChange(booking.id, s); setOpen(false); }}
                  className={cn("w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors", booking.status === s && "font-semibold")}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
